<script setup lang="ts">
import { BadgeCheck, Bell, ChevronsUpDown, LogOut } from 'lucide-vue-next';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import '/node_modules/flag-icons/css/flag-icons.min.css';
import api from '@/api/api';
import { useAccountStore } from '@/store/AccountStore';

const accountStore = useAccountStore();
import { Moon, Sun, Check } from 'lucide-vue-next';
import { useColorMode } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

const mode = useColorMode();
const { t, locale } = useI18n();

const langToCountry: Record<string, string> = {
  'en-US': 'gb',
  'sv-SE': 'se',
};

const setLang = (lang: string) => {
  locale.value = lang;
  accountStore.updateProfile({ language: lang });
};

const setTheme = (theme: 'light' | 'dark') => {
  mode.value = theme;
  accountStore.updateProfile({ theme });
};

defineProps<{
  user: {
    name: string;
    email: string;
    avatar: string | null;
  };
}>();
const { isMobile } = useSidebar();

const logout = () => {
  api
    .request('auth/logout')
    .fetch()
    .then((resp) => {
      accountStore.clearAuthState();
      console.debug('Logout successful:', resp);
    })
    .catch((error) => {
      console.error('Logout failed:', error);
    });
};
</script>
<template>
  <SidebarMenu>
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <SidebarMenuButton size="lg" class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
            <Avatar class="h-8 w-8 rounded-lg">
              <AvatarImage :src="user.avatar ?? ''" :alt="user.name" />
              <AvatarFallback class="rounded-lg"> {{ user.name.charAt(0) }}{{ user.name.split(' ')[1]?.charAt(0) || '' }} </AvatarFallback>
            </Avatar>
            <div class="grid flex-1 text-left text-sm leading-tight">
              <span class="truncate font-semibold">{{ user.name }}</span>
              <span class="truncate text-xs">{{ user.email }}</span>
            </div>
            <ChevronsUpDown class="ml-auto size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="w-[--reka-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          :side="isMobile ? 'bottom' : 'right'"
          align="end"
          :side-offset="4"
        >
          <DropdownMenuLabel class="p-0 font-normal">
            <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar class="h-8 w-8 rounded-lg">
                <AvatarImage :src="user.avatar ?? ''" :alt="user.name" />
                <AvatarFallback class="rounded-lg"> {{ user.name.charAt(0) }}{{ user.name.split(' ')[1]?.charAt(0) || '' }} </AvatarFallback>
              </Avatar>
              <div class="grid flex-1 text-left text-sm leading-tight">
                <span class="truncate font-semibold">{{ user.name }}</span>
                <span class="truncate text-xs">{{ user.email }}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <span :class="`fi fi-${langToCountry[locale] ?? 'se'} mr-2`" aria-hidden="true"></span>
              {{ locale === 'en-US' ? 'English' : 'Svenska' }}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem :disabled="locale === 'en-US'" @select.prevent="() => setLang('en-US')">
                  <span :class="`fi fi-${langToCountry['en-US']} mr-2`" aria-hidden="true"></span>
                  English
                  <Check class="size-4 ml-auto" :class="locale === 'en-US' ? 'opacity-100' : 'opacity-0'" />
                </DropdownMenuItem>
                <DropdownMenuItem :disabled="locale === 'sv-SE'" @select.prevent="() => setLang('sv-SE')">
                  <span :class="`fi fi-${langToCountry['sv-SE']} mr-2`" aria-hidden="true"></span>
                  Svenska
                  <Check class="size-4 ml-auto" :class="locale === 'sv-SE' ? 'opacity-100' : 'opacity-0'" />
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Sun v-if="mode === 'light'" class="size-4 mr-2" />
              <Moon v-else class="size-4 mr-2" />
              {{ mode === 'light' ? t('settings.light_mode') : t('settings.dark_mode') }}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem :disabled="mode === 'light'" @select.prevent="() => setTheme('light')">
                  <Sun class="size-4 mr-2" />
                  {{ t('settings.light_mode') }}
                  <Check class="size-4 ml-auto" :class="mode === 'light' ? 'opacity-100' : 'opacity-0'" />
                </DropdownMenuItem>
                <DropdownMenuItem :disabled="mode === 'dark'" @select.prevent="() => setTheme('dark')">
                  <Moon class="size-4 mr-2" />
                  {{ t('settings.dark_mode') }}
                  <Check class="size-4 ml-auto" :class="mode === 'dark' ? 'opacity-100' : 'opacity-0'" />
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuGroup>
            <DropdownMenuItem>
              <BadgeCheck />
              {{ t('settings.account') }}
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Bell />
              {{ t('settings.notifications') }}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="logout">
            <LogOut />
            {{ t('settings.logout') }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  </SidebarMenu>
</template>
